// Typed access to the header wallet (wwwroot/js/earnings.js). The counter lives in the Razor chrome so
// bundle-less pages show it too, and the SPA reaches it through a global; this is the only file that
// touches it. Everything no-ops when the script isn't loaded: a repair never depends on the wallet.

export type AwardResult = {
  deltaCents: number;
  fullAmountCents: number;
  balanceCents: number;
};

type WalletApi = {
  balanceCents: () => number;
  isGuest: () => boolean;
  successCents: (swingPoints: number, priorSuccesses: number, hintUsed?: boolean) => number;
  failureCents: (swingPoints: number) => number;
  award: (opts: {
    success: boolean;
    swingPoints: number;
    priorSuccesses: number;
    hintUsed?: boolean;
    lossPoints?: number | null;
    anchor?: Element | null;
  }) => AwardResult | null;
  syncMirrors: () => void;
  syncFromServer: (result: { BalanceCents?: number }) => void;
  refresh: () => Promise<unknown>;
};

declare global {
  interface Window {
    scEarnings?: WalletApi;
  }
}

function wallet(): WalletApi | null {
  return typeof window === 'undefined' ? null : (window.scEarnings ?? null);
}

// Pays or charges for one graded attempt and runs the celebration. `priorSuccesses` is the count
// before this attempt (passing the post-attempt count would underpay). `anchor` is where the chip
// flies from; without it, the chip rises from the counter.
export function awardForAttempt(opts: {
  success: boolean;
  swingPoints: number;
  priorSuccesses: number;
  // The player took the hint: halves a repair's pay, costs nothing extra on a failure.
  hintUsed?: boolean;
  // What a failed attempt costs: the played move's drop on this position. Omit when ungraded; the
  // mistake's swing then stands in.
  lossPoints?: number | null;
  anchor?: Element | null;
}): AwardResult | null {
  const api = wallet();
  if (!api) return null;
  try {
    return api.award(opts);
  } catch {
    return null;
  }
}

// Reconciles the counter with the server's balance, silently (the animation already ran).
export function syncWalletFromServer(result: { BalanceCents?: number } | null | undefined): void {
  const api = wallet();
  if (!api || !result) return;
  try {
    api.syncFromServer(result);
  } catch {
    /* the counter is cosmetic; never let it break the repair flow */
  }
}

// Paints the live balance onto a just-mounted mirror, which ships as static "0.00".
export function syncWalletMirrors(): void {
  const api = wallet();
  if (!api?.syncMirrors) return;
  try {
    api.syncMirrors();
  } catch {
    /* cosmetic */
  }
}

export function walletBalanceCents(): number {
  const api = wallet();
  return api ? api.balanceCents() : 0;
}

// What repairing this mistake would pay now, from the same ladder as the award.
export function previewSuccessCents(
  swingPoints: number,
  priorSuccesses: number,
  hintUsed = false,
): number {
  const api = wallet();
  if (api) {
    try {
      return api.successCents(swingPoints, priorSuccesses, hintUsed);
    } catch {
      /* fall through to the local copy */
    }
  }
  let cents = Math.max(Math.trunc(swingPoints), 0);
  if (cents === 0) return 0;
  for (let i = 0; i < priorSuccesses && cents > 1; i++) {
    cents = Math.round(cents / 2);
  }
  if (hintUsed && cents > 1) {
    cents = Math.round(cents / 2);
  }
  return Math.max(cents, 1);
}

export function formatMoney(cents: number): string {
  return `$${(Math.abs(cents) / 100).toFixed(2)}`;
}
